import { deckLineTypeKey } from "./deckGrouping";

const SPHERE_ORDER = ["Leadership", "Tactics", "Spirit", "Lore", "Neutral"];

/** @returns {string} Stable key for sphere filter (canonical sphere name, or trimmed label). */
export function canonicalSphereName(sphereName) {
  const raw = (sphereName || "").trim();
  if (!raw) return "—";
  const found = SPHERE_ORDER.find((s) => s.toLowerCase() === raw.toLowerCase());
  return found ?? raw;
}

function compareSphereKeys(a, b) {
  if (a === "—") return 1;
  if (b === "—") return -1;
  const ia = SPHERE_ORDER.indexOf(a);
  const ib = SPHERE_ORDER.indexOf(b);
  const ta = ia === -1 ? 2 : 1;
  const tb = ib === -1 ? 2 : 1;
  if (ta !== tb) return ta - tb;
  if (ta === 1) return ia - ib;
  return a.localeCompare(b, undefined, { sensitivity: "base" });
}

/** Spheres that appear on non-hero cards in the deck, with copy counts. */
export function spheresInDeck(lines) {
  const counts = new Map();
  for (const line of lines || []) {
    if (deckLineTypeKey(line.card?.type_name) === "hero") continue;
    const qty = line.quantity ?? 0;
    if (qty <= 0) continue;
    const key = canonicalSphereName(line.card?.sphere_name);
    counts.set(key, (counts.get(key) || 0) + qty);
  }
  return [...counts.entries()]
    .sort(([a], [b]) => compareSphereKeys(a, b))
    .map(([key, count]) => ({
      key,
      label: key === "—" ? "No sphere" : key,
      count,
    }));
}

function lineMatchesSphere(line, sphereKey) {
  if (sphereKey == null) return true;
  return canonicalSphereName(line.card?.sphere_name) === sphereKey;
}

/** Maps card.cost to a bucket key used for grouping (number or string label). */
export function costBucketKey(cost) {
  if (cost == null || cost === "") return "—";
  const raw = String(cost).trim();
  if (/^\d+$/.test(raw)) return parseInt(raw, 10);
  return raw;
}

function bucketSortKey(key) {
  if (typeof key === "number") return [0, key];
  if (key === "—") return [2, 0];
  return [1, String(key).toLowerCase()];
}

function compareBucketKeys(a, b) {
  const ka = bucketSortKey(a);
  const kb = bucketSortKey(b);
  if (ka[0] !== kb[0]) return ka[0] - kb[0];
  if (ka[0] === 0) return ka[1] - kb[1];
  return ka[1].localeCompare(kb[1]);
}

export function formatCostBucketLabel(key) {
  return typeof key === "number" ? String(key) : key;
}

/**
 * Count cards by printed cost for a deck's lines. Heroes are excluded (same as deck size).
 * @param {object} [options]
 * @param {string|null} [options.sphere] Canonical sphere key, or null/undefined for all spheres.
 * @returns {{ buckets: { key: number|string, count: number, label: string }[], total: number, maxCount: number }}
 */
export function computeCostCurve(lines, { sphere = null } = {}) {
  const counts = new Map();

  for (const line of lines || []) {
    if (deckLineTypeKey(line.card?.type_name) === "hero") continue;
    if (!lineMatchesSphere(line, sphere)) continue;
    const qty = line.quantity ?? 0;
    if (qty <= 0) continue;

    const key = costBucketKey(line.card?.cost);
    counts.set(key, (counts.get(key) || 0) + qty);
  }

  const keys = [...counts.keys()].sort(compareBucketKeys);
  const buckets = keys.map((key) => ({
    key,
    label: formatCostBucketLabel(key),
    count: counts.get(key),
  }));
  const total = buckets.reduce((sum, b) => sum + b.count, 0);
  const maxCount = buckets.reduce((max, b) => Math.max(max, b.count), 0);

  return { buckets, total, maxCount };
}
